import { ApolloError } from "@apollo/client";
import type { GraphQLFormattedError } from "graphql";
import { z } from "zod";
import {
  type OverCapacityLimit,
  useOverCapacityStore,
} from "@/domains/appointment/appointments/store/use-over-capacity-store";

const CAPACITY_EXCEEDED_CODE = "capacityExceeded";

const overCapacityMetadataSchema = z.object({
  limits: z
    .array(
      z.object({
        type: z.enum(["TIME_RANGE", "DAY", "CONCURRENT"]),
        limit: z.number(),
        confirmedCount: z.number(),
        rangeStart: z.string(),
        rangeEnd: z.string(),
      })
    )
    .min(1),
});

function toGraphQLErrors(errors: unknown): readonly GraphQLFormattedError[] {
  if (errors instanceof ApolloError) {
    return errors.graphQLErrors;
  }
  return Array.isArray(errors) ? errors : [];
}

export function readOverCapacityLimits(errors: unknown): OverCapacityLimit[] | null {
  const error = toGraphQLErrors(errors).find(
    (candidate) => candidate.extensions?.code === CAPACITY_EXCEEDED_CODE
  );
  if (!error) {
    return null;
  }
  const parsed = overCapacityMetadataSchema.safeParse(error.extensions?.metadata);
  return parsed.success ? parsed.data.limits : null;
}

export interface OverCapacityAcknowledgement {
  acknowledgeOverCapacity?: true;
}

export async function sendWithOverCapacityApproval<R extends { errors?: unknown }>(
  send: (acknowledgement: OverCapacityAcknowledgement) => Promise<R>
): Promise<R | null> {
  let limits: OverCapacityLimit[] | null;
  try {
    const result = await send({});
    limits = readOverCapacityLimits(result.errors);
    if (!limits) {
      return result;
    }
  } catch (error) {
    limits = readOverCapacityLimits(error);
    if (!limits) {
      throw error;
    }
  }

  const accepted = await useOverCapacityStore.getState().ask(limits);
  return accepted ? send({ acknowledgeOverCapacity: true }) : null;
}
